import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { getBullseyeRelayRoster } from "../../lib/leaderboardApi";
import BullseyeRelayScores from "./BullseyeRelayScores";
import { buildBullseyeRelayScores } from "./bullseyeRelayScoreModel";
import "./BullseyeScoreView.css";

export function MatchScoreView({ family, ...props }) {
  return family === "bullseye" ? <BullseyeScoreView {...props} /> : props.children;
}

// This view follows the route's refresh revision; it never starts another timer.
export default function BullseyeScoreView({ match, scoreRows = [], setup, refreshRevision = 0,
  active = true, scoresLoading = false, scoresError = "", onRefresh, completed = false, children }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const relayView = params.get("scoresView") === "relay";
  const [visited, setVisited] = useState(relayView);
  const [state, setState] = useState({ matchId: match.matchId, data: null, loading: false, error: "" });
  const [retry, setRetry] = useState(0);
  const requestId = useRef(0);
  const search = params.get("relaySearch") || "";
  const relay = params.get("relay") || "all";
  const firearmGroup = params.get("relayGroup") || "all";
  const enabled = active && relayView;
  useEffect(() => { if (relayView) setVisited(true); }, [relayView]);
  useEffect(() => {
    if (!enabled) return undefined;
    const id = ++requestId.current;
    const controller = new AbortController();
    setState(previous => ({ matchId: match.matchId, data: previous.matchId === match.matchId ? previous.data : null, loading: true, error: "" }));
    getBullseyeRelayRoster(match.matchId, { signal: controller.signal }).then(data => {
      if (!controller.signal.aborted && id === requestId.current) setState({ matchId: match.matchId, data, loading: false, error: "" });
    }).catch(error => {
      if (!controller.signal.aborted && id === requestId.current) setState(previous => ({ ...previous, loading: false, error: "Relay assignments couldn't refresh. Showing the last available assignments when available." }));
    });
    return () => controller.abort();
  }, [match.matchId, enabled, refreshRevision, retry]);
  const roster = state.matchId === match.matchId ? state.data : null;
  const assignmentError = state.matchId === match.matchId ? state.error : "";
  const model = useMemo(() => buildBullseyeRelayScores({ matchId: match.matchId, roster, scoreRows: !roster && !assignmentError ? [] : scoreRows, setup,
    relays: match.operations?.relays || [], search, relay, firearm: firearmGroup }),
  [match.matchId, match.operations?.relays, roster, scoreRows, setup, search, relay, firearmGroup, assignmentError]);
  function update(patch, replace = true) {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => value && value !== "all" ? next.set(key, value) : next.delete(key));
    setParams(next, { replace, state: location.state });
  }
  function refresh() { setRetry(value => value + 1); onRefresh?.(); }
  return <div className="bullseye-score-view">
    <div className="bullseye-score-view-switch" role="group" aria-label="Score view">
      <button type="button" aria-pressed={!relayView} onClick={() => update({ scoresView: "", result: "", participant: "", resultSection: "" }, false)}>Leaderboard</button>
      <button type="button" aria-pressed={relayView} onClick={() => update({ scoresView: "relay", result: "", participant: "", resultSection: "" }, false)}>By relay &amp; target</button>
    </div>
    <div hidden={relayView}>{children}</div>
    {(visited || relayView) && <div hidden={!relayView}><BullseyeRelayScores matchId={match.matchId} model={model}
      loading={state.loading || (!roster && !assignmentError && enabled)} error={assignmentError} scoresLoading={scoresLoading} scoresError={scoresError}
      search={search} relay={relay} firearmGroup={firearmGroup} completed={completed} onRefresh={refresh}
      onFiltersChange={patch => update({ ...(patch.search !== undefined ? { relaySearch: patch.search } : {}),
        ...(patch.relay !== undefined ? { relay: patch.relay } : {}), ...(patch.firearmGroup !== undefined ? { relayGroup: patch.firearmGroup } : {}) })}
      scoreState={{ ...location.state, resultsPath: location.pathname + location.search }} /></div>}
  </div>;
}
